import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Divider, Typography, Slider, Select } from 'antd';
import { ArrowRightOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, ButtonGroup } from '@mui/material';
import Alert from "@mui/material/Alert";
import BlacklistInstance from "../../../Blacklist/components/BlacklistInstance";

const { Option } = Select;

const getEtfsBelowTolerance = (etfs, minWeightTolerance) => {
  const keys = Object.keys(etfs).filter(x => etfs[x] < minWeightTolerance);
  if (!keys || keys.length === 0) return null;
  return keys.map(key => ({
    symbol: key,
    contribution: etfs[key],
    difference: minWeightTolerance - etfs[key]
  }));
};

const SmaSider = ({
  onSubmit,
  blacklists,
  screens,
  config,
  updateConfig,
  etfContributions
}) => {
  const [blaclistDrawerVisible, setBlacklistDrawerVisible] = useState(false);
  const navigate = useNavigate();

  // Determine if we’re building a “multi-asset” SMA
  const atLeastOneNotReplicated = Object.keys(config.etfs).some(
    etf => config.etfs[etf].doNotReplicate
  );
  const etfsNotReplicated = Object.keys(config.etfs).filter(
    etf => config.etfs[etf].doNotReplicate
  );

  let etfsBelowTolerance = getEtfsBelowTolerance(
    etfContributions,
    config.minWeightTolerance * 0.01
  );
  if (etfsBelowTolerance && etfsNotReplicated.length > 0) {
    etfsBelowTolerance = etfsBelowTolerance.filter(
      etf => !etfsNotReplicated.includes(etf.symbol)
    );
  }

  // ------------------
  // LOCAL STATE SLIDERS
  // ------------------

  // 1) CUT-OFF PERCENT (0-100 range)
  //    We'll default to config.cutoffWeight || 80 if it’s not defined
  const [localCutoff, setLocalCutoff] = useState(config.cutoffWeight || 80);

  // 2) MIN REPRESENTATION (0-20 range)
  const [localMinWeight, setLocalMinWeight] = useState(
    config.minWeightTolerance || 0
  );

  // 3) MAX PER HOLDING (0-20 range, displayed in %)
  //    config.maxPerHolding is stored e.g. as 0.05 for 5%
  //    so we multiply by 100 for the slider
  const [localMaxPerHolding, setLocalMaxPerHolding] = useState(
    (config.maxPerHolding || 0) * 100
  );

  return (
    <div className={"round-2 overflow-y-auto hide-scrollbar px-2"}>
      {atLeastOneNotReplicated && (
        <>
          <Alert
            variant="filled"
            severity="error"
            sx={{
              padding: "0px 8px",
              fontSize: "0.75rem",
              "& .MuiAlert-icon": { fontSize: "0.8rem" }
            }}
          >
            Building Multi-Asset SMA
          </Alert>
          <Divider />
        </>
      )}

      {/* FILL TYPE BUTTONS */}
      <ButtonGroup>
        <Button
          variant={config?.fillType === "equal" ? "contained" : "outlined"}
          onClick={() => updateConfig("fillType", "equal")}
          size={"small"}
        >
          Equal
        </Button>
        <Button
          variant={config?.fillType === "prorata" ? "contained" : "outlined"}
          onClick={() => updateConfig("fillType", "prorata")}
          size={"small"}
        >
          Pro Rata
        </Button>
        <Button
          variant={config?.fillType === "sectors" ? "contained" : "outlined"}
          onClick={() => updateConfig("fillType", "sectors")}
          size={"small"}
        >
          Sectors
        </Button>
      </ButtonGroup>

      <Divider />

      {/* BLACKLIST SELECT */}
      <Select
        showSearch
        style={{ width: "100%" }}
        placeholder="Select a Blacklist"
        optionFilterProp="children"
        onChange={(value) => updateConfig("blacklistId", value)}
        value={config.blacklistId}
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        suffixIcon={
          <>
            <span>{blacklists.length}</span>
            <PlusOutlined
              style={{ color: "#029EFF" }}
              onClick={() => setBlacklistDrawerVisible(true)}
            />
          </>
        }
      >
        {blacklists.map((blacklist) => (
          <Option key={blacklist.id} value={blacklist.id}>
            {blacklist.title}
          </Option>
        ))}
      </Select>

      {/* SHOW "REMOVE" IF A BLACKLIST IS SELECTED */}
      {config.blacklistId && (
        <Typography.Text
          type="danger"
          onClick={() => updateConfig("blacklistId", null)}
          style={{ cursor: "pointer" }}
        >
          Remove
        </Typography.Text>
      )}

      <Divider className={"borderless"} />

      {/* SCREENER SELECT */}
      <Select
        showSearch
        style={{ width: "100%" }}
        placeholder="Select a Screen"
        optionFilterProp="children"
        onChange={(value) => updateConfig("screenerId", value)}
        value={config.screenerId}
        filterOption={(input, option) =>
          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        suffixIcon={
          <>
            <span>{screens.length}</span>
            <ArrowRightOutlined
              onClick={() => navigate("/screener")}
              style={{ color: "#029EFF" }}
            />
          </>
        }
      >
        {screens.map((screen) => (
          <Option key={screen.id} value={screen.id}>
            {screen.title}
          </Option>
        ))}
      </Select>

      {/* SHOW "REMOVE" IF A SCREENER IS SELECTED */}
      {config.screenerId && (
        <Typography.Text
          type="danger"
          onClick={() => updateConfig("screenerId", null)}
          style={{ cursor: "pointer" }}
        >
          Remove
        </Typography.Text>
      )}

      <Divider className={"borderless"} />

      {/* ------------------------------------ */}
      {/* 1) CUTOFF PERCENT SLIDER (30-100)     */}
      {/* ------------------------------------ */}
      <Typography.Text>Cutoff Percent: {localCutoff}%</Typography.Text>
      <Slider
        min={30}
        max={100}
        step={2.5}
        value={localCutoff}
        onChange={(value) => {
          setLocalCutoff(value);
        }}
        onAfterChange={(finalValue) => {
          updateConfig("cutoffWeight", finalValue);
        }}
      />

      <Divider />

      {/* ------------------------------------ */}
      {/* 2) MIN REPRESENTATION SLIDER (0-20)   */}
      {/* ------------------------------------ */}
      <Typography.Text>Min Representation: {localMinWeight}%</Typography.Text>
      <Slider
        min={0}
        max={20}
        step={0.25}
        value={localMinWeight}
        onChange={(value) => {
          setLocalMinWeight(value);
        }}
        onAfterChange={(finalValue) => {
          updateConfig("minWeightTolerance", finalValue);
        }}
      />

      <Divider />

      {/* ------------------------------------ */}
      {/* 3) MAX PER HOLDING SLIDER (0-20),     */}
      {/*    displayed as X%, but config is a   */}
      {/*    fraction.                          */}
      {/* ------------------------------------ */}
      <Typography.Text>
        Max Per Holding: {localMaxPerHolding}%
      </Typography.Text>
      <Slider
        min={0}
        max={20}
        step={0.25}
        // localMaxPerHolding is in 0..20 range
        value={localMaxPerHolding}
        onChange={(value) => {
          setLocalMaxPerHolding(value);
        }}
        onAfterChange={(finalValue) => {
          // finalValue is 0..20 => config wants fraction => finalValue * 0.01
          updateConfig("maxPerHolding", finalValue * 0.01);
        }}
      />

      <Divider />

      {config.fillType === "sectors" && (
        <>
          <Typography.Text>
            Sector Tolerance: {config?.minSectorTolerance * 100 || 0}%
          </Typography.Text>
          <Slider
            min={0.25}
            max={10}
            step={0.25}
            value={config?.minSectorTolerance * 100 || 0}
            onChange={(value) => {
              updateConfig("minSectorTolerance", value * 0.01);
            }}
          />
        </>
      )}

      {blaclistDrawerVisible && (
        <BlacklistInstance onClose={() => setBlacklistDrawerVisible(false)} />
      )}
    </div>
  );
};

export default SmaSider;
